import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Link, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { useConnections } from 'api/connectors';
import { useStats } from 'api/bi';
import { usePipelines, usePipelinesHealth } from 'api/pipelines';
import { useDbtProjects, useWarehouseOverview, useWarehouses } from 'api/warehouse';
import useAuthStore from 'stores/authStore';
import useRole from 'hooks/useRole';
import { useTokens } from 'theme';
import { formatDate, formatNumber } from 'i18n/format';
import { getLoadSummary, getPreparationSummary, getLoadMetrics, getSourceMetrics } from './dashboardStatus';

const LOADS_URL = '/console/pipelines';
const PREPARATION_URL = '/console/pipelines?tab=preparation';
const STORAGE_URL = '/console/pipelines?tab=storage';
const whenLabel = (value) => formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });
const runTime = (run) => run.finished_at || run.started_at || run.created_at;

function greeting(hour, t) {
  if (hour >= 5 && hour < 12) return t('shell.goodMorning');
  if (hour >= 12 && hour < 17) return t('shell.goodAfternoon');
  if (hour >= 17 && hour < 23) return t('shell.goodEvening');
  return t('shell.goodNight');
}

function runStatus(status, t) {
  const known = {
    success: { label: t('shell.done'), color: 'success' },
    failed: { label: t('shell.error'), color: 'error' },
    running: { label: t('shell.inProgress'), color: 'info' },
    pending: { label: t('shell.queued'), color: 'default' },
    cancelled: { label: t('shell.cancelled'), color: 'warning' },
  };
  return known[status] || { label: t('data.workspaceHome.statusUnknown', { defaultValue: 'Status unavailable' }), color: 'default' };
}

function preparationAction(command, t) {
  const actions = {
    'dbt build': t('data.workspaceHome.actionBuild', { defaultValue: 'Data preparation' }),
    'dbt run': t('data.workspaceHome.actionRun', { defaultValue: 'Data update' }),
    'dbt test': t('data.workspaceHome.actionTest', { defaultValue: 'Data check' }),
    'dbt seed': t('data.workspaceHome.actionSeed', { defaultValue: 'Reference data update' }),
    'dbt snapshot': t('data.workspaceHome.actionSnapshot', { defaultValue: 'History update' }),
  };
  return actions[command] || actions['dbt build'];
}

function LoadStatus({ summary }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  const { isAdmin } = useRole();
  const states = {
    checking: [
      t('data.workspaceHome.loadsChecking', { defaultValue: 'Checking loads' }),
      t('data.workspaceHome.loadsCheckingText', { defaultValue: 'Getting the current status of your loads.' }),
    ],
    unavailable: [
      t('data.workspaceHome.loadsUnavailable', { defaultValue: 'Could not check load status' }),
      t('data.workspaceHome.loadsUnavailableText', { defaultValue: 'Some status information could not be retrieved. Open loads to check the details.' }),
    ],
    unknown: [
      t('data.workspaceHome.loadsUnknown', { defaultValue: 'Load status is incomplete' }),
      t('data.workspaceHome.loadsUnknownText', { defaultValue: 'There is not enough information to confirm the status of every load.' }),
    ],
    empty: [
      t('data.workspaceHome.loadsEmpty', { defaultValue: 'Set up your first load' }),
      t('data.workspaceHome.loadsEmptyText', { defaultValue: 'Choose a source to start loading data into your workspace.' }),
    ],
    off: [
      t('data.workspaceHome.loadsOff', { defaultValue: 'Loads are switched off' }),
      t('data.workspaceHome.loadsOffText', { defaultValue: 'Automatic updates are disabled. You can enable them in loads.' }),
    ],
    waiting: [
      t('data.workspaceHome.loadsWaiting', { defaultValue: 'Waiting for the first load' }),
      t('data.workspaceHome.loadsWaitingText', { defaultValue: 'Some loads have not run yet. Open loads to review their schedule.' }),
    ],
    processing: [
      t('data.workspaceHome.loadsProcessing', { defaultValue: 'Data is loading' }),
      t('data.workspaceHome.loadsProcessingText', { defaultValue: 'A load is running or queued. Follow its progress in loads.' }),
    ],
    healthy: [
      t('data.workspaceHome.loadsHealthy', { defaultValue: 'Active loads have no failures or delays' }),
      t('data.workspaceHome.loadsHealthyText', { defaultValue: 'This is the status of your loads. Data preparation is shown separately below.' }),
    ],
    attention: [t('data.workspaceHome.loadsAttention', { defaultValue: 'A load needs attention' })],
  };
  const [title, text] = states[summary.state];
  const problem = summary.problems?.[0];
  const reasons = {
    billing: t('data.workspaceHome.problemBilling', { defaultValue: 'Updates are paused because of the subscription.' }),
    source: t('data.workspaceHome.problemSource', { defaultValue: 'Source access needs to be checked.' }),
    failed: t('data.workspaceHome.problemFailed', { defaultValue: 'The latest load failed. Open it to see the reason and next step.' }),
    paused: t('data.workspaceHome.problemPaused', { defaultValue: 'The load is paused and needs attention.' }),
    late: t('data.workspaceHome.problemLate', { defaultValue: 'The expected update is delayed.' }),
  };
  const sourceFix = problem?.reason === 'source' && problem.pipeline.source_connection?.public_id;
  const canCreate = summary.state === 'empty' && isAdmin;
  const href = sourceFix ? '/console/connectors/' + problem.pipeline.source_connection.public_id
    : problem ? LOADS_URL + '/' + problem.pipeline.public_id
      : canCreate ? '/console/pipelines/new' : LOADS_URL;
  const action = sourceFix ? t('data.workspaceHome.checkSource', { defaultValue: 'Check source access' })
    : problem ? t('data.workspaceHome.openLoad', { defaultValue: 'Open load' })
      : canCreate ? t('data.workspaceHome.createLoad', { defaultValue: 'Create load' })
        : t('data.workspaceHome.viewLoads', { defaultValue: 'View loads' });
  const good = summary.state === 'healthy';
  const concern = ['attention', 'unavailable', 'unknown'].includes(summary.state);

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: concern ? tokens.warningBg : tokens.surface }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ mt: 0.25, color: good ? tokens.positive : concern ? tokens.warning : tokens.inkMuted }}>
          {summary.state === 'checking' ? <CircularProgress size={24} />
            : good ? <CheckCircleOutlineIcon /> : concern ? <ErrorOutlineIcon /> : <SyncOutlinedIcon />}
        </Box>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }} aria-live="polite">
          <Typography component="h2" sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 0.5 }}>{title}{problem ? ` · ${problem.pipeline.name}` : ''}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.inkMuted }}>{problem ? reasons[problem.reason] : text}</Typography>
          {summary.problems?.length > 1 && (
            <Typography sx={{ fontSize: '0.8rem', color: tokens.inkMuted, mt: 0.75 }}>
              {t('data.workspaceHome.attentionCount', { defaultValue: 'Loads needing attention: {{amount}}', amount: summary.problems.length })}
            </Typography>
          )}
          {!!summary.offCount && summary.state !== 'off' && (
            <Typography sx={{ fontSize: '0.8rem', color: tokens.inkMuted, mt: 0.75 }}>
              {t('data.workspaceHome.offCount', { defaultValue: 'Switched off: {{amount}}', amount: summary.offCount })}
            </Typography>
          )}
        </Box>
        <Button component={RouterLink} to={href} variant={problem || canCreate ? 'contained' : 'outlined'} size="small" sx={{ flexShrink: 0 }}>{action}</Button>
      </Box>
    </Paper>
  );
}

function RunRow({ name, detail, status, to }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  const state = runStatus(status, t);
  return (
    <Box component={RouterLink} to={to} sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5,
      borderTop: '1px solid ' + tokens.border, color: 'inherit', textDecoration: 'none',
      '&:hover': { bgcolor: tokens.highlight },
      '&:focus-visible': { outline: '2px solid ' + tokens.focus, outlineOffset: -2 },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, overflowWrap: 'anywhere' }}>{name}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: tokens.inkMuted }}>{detail}</Typography>
      </Box>
      <Chip label={state.label} size="small" color={state.color} />
    </Box>
  );
}

function RunPanel({ title, linkLabel, linkTo, empty, loading, error, children, notice }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  return (
    <Paper sx={{ overflow: 'hidden', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2.5, py: 1.75 }}>
        <Typography component="h2" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{title}</Typography>
        <Link component={RouterLink} to={linkTo} underline="hover" sx={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.link, flexShrink: 0 }}>{linkLabel}</Link>
      </Box>
      {notice}
      {error ? (
        <Alert severity="warning" sx={{ mx: 2.5, mb: 2 }}>{t('data.workspaceHome.historyUnavailable', { defaultValue: 'Recent activity could not be retrieved.' })}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : React.Children.count(children) ? children : (
        <Typography sx={{ px: 2.5, py: 2, fontSize: '0.85rem', color: tokens.inkMuted, borderTop: '1px solid ' + tokens.border }}>{empty}</Typography>
      )}
    </Paper>
  );
}

function PreparationNotice({ summary }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  const labels = {
    checking: t('data.workspaceHome.preparationChecking', { defaultValue: 'Checking data preparation…' }),
    unavailable: t('data.workspaceHome.preparationUnavailable', { defaultValue: 'Data preparation status could not be retrieved.' }),
    unknown: t('data.workspaceHome.preparationUnknown', { defaultValue: 'Data preparation status is incomplete.' }),
    empty: t('data.workspaceHome.preparationEmpty', { defaultValue: 'Data preparation has not been configured yet.' }),
    off: t('data.workspaceHome.preparationOff', { defaultValue: 'Data preparation is switched off.' }),
    waiting: t('data.workspaceHome.preparationWaiting', { defaultValue: 'Waiting for the first preparation run.' }),
    processing: t('data.workspaceHome.preparationProcessing', { defaultValue: 'Data preparation is running or queued.' }),
    attention: t('data.workspaceHome.preparationAttention', { defaultValue: 'Data preparation needs attention.' }),
  };
  // Successful project runs do not establish report freshness or readiness.
  if (summary.state === 'completed') return null;
  if (summary.state === 'attention') {
    return (
      <Alert severity="warning" sx={{ mx: 2.5, mb: 2 }}>
        <Typography variant="body2">{labels.attention}</Typography>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{summary.projects.map((project) => project.name).join(', ')}</Typography>
        <Link component={RouterLink} to={PREPARATION_URL} color="inherit" sx={{ display: 'inline-block', mt: 0.75 }}>
          {t('data.workspaceHome.reviewPreparation', { defaultValue: 'Review preparation' })}
        </Link>
      </Alert>
    );
  }
  return <Typography sx={{ px: 2.5, pb: 1.5, fontSize: '0.8rem', color: tokens.inkMuted }}>{labels[summary.state]}</Typography>;
}

function formatBytes(bytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const unit = bytes > 0 ? Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1) : 0;
  return formatNumber(bytes / (1024 ** unit), { maximumFractionDigits: 1 }) + ' ' + units[unit];
}

function MetricCard({ label, icon: Icon, accent = false, to, children }) {
  const tokens = useTokens();
  return (
    <Paper component={to ? RouterLink : 'section'} to={to} aria-label={label} sx={{
      p: 2.5, height: '100%', minHeight: 210, minWidth: 0,
      display: 'flex', flexDirection: 'column', gap: 1.5,
      bgcolor: accent ? tokens.accent : tokens.surface,
      color: accent ? tokens.onAccent : tokens.ink, textDecoration: 'none',
      '&:focus-visible': { outline: `2px solid ${tokens.focus}`, outlineOffset: 3 },
      ...(to ? { '&:hover': { boxShadow: 2 } } : {}),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: accent ? tokens.accentHover : tokens.raised }}>
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Typography component="h2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{label}</Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }} aria-live="polite">{children}</Box>
    </Paper>
  );
}

function MetricValue({ children }) {
  return <Typography sx={{ fontSize: '2.1rem', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.25, mb: 1, fontVariantNumeric: 'tabular-nums' }}>{children}</Typography>;
}

function DashboardMetrics({ connections, pipelines, health }) {
  const { t } = useTranslation();
  const tokens = useTokens();
  const sources = getSourceMetrics(connections);
  const loads = getLoadMetrics(pipelines, health);
  const pending = t('data.workspaceHome.metricsChecking');
  const unknown = t('data.workspaceHome.metricsUnknown');
  const sourceDetail = sources.total !== null
    ? sources.total === 0 ? t('data.workspaceHome.metricNoSources') : t('data.workspaceHome.sourcesActive', { active: formatNumber(sources.active), total: formatNumber(sources.total) })
    : connections.isLoading ? pending : unknown;
  const loadDetail = loads.total === 0 ? t('data.workspaceHome.metricNoLoads') : getLoadSummary(pipelines, health).state === 'off' ? t('data.workspaceHome.loadsOff') : loads.problems !== null
    ? loads.problems ? t('data.workspaceHome.metricProblems', { amount: formatNumber(loads.problems) }) : t('data.workspaceHome.metricNoProblems')
    : loads.knownProblems ? t('data.workspaceHome.metricKnownProblems', { amount: formatNumber(loads.knownProblems) }) : unknown;
  const latest = loads.latestDataAt;
  const when = latest ? formatDate(latest, { hour: '2-digit', minute: '2-digit' }) : '—';
  const latestDetail = latest ? formatDate(latest, { dateStyle: 'medium' })
    : loads.healthComplete ? t('data.workspaceHome.noSuccessfulLoadInHistory')
      : pipelines.isLoading || health.isLoading ? pending : unknown;
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard label={t('shell.sources')} icon={CableOutlinedIcon} accent to="/console/connectors">
          <MetricValue>{sources.total !== null ? formatNumber(sources.total) : '—'}</MetricValue>
          <Typography variant="body2">{sourceDetail}</Typography>
        </MetricCard>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}><StorageSummary /></Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard label={t('shell.loads')} icon={SyncOutlinedIcon} to={LOADS_URL}>
          <MetricValue>{loads.total !== null ? formatNumber(loads.total) : '—'}</MetricValue>
          <Typography variant="body2" sx={{ color: loads.knownProblems ? tokens.warning : tokens.inkMuted }}>{pipelines.isLoading ? pending : loadDetail}</Typography>
        </MetricCard>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard label={t('data.workspaceHome.lastSuccessfulLoad')} icon={ScheduleOutlinedIcon} to={LOADS_URL}>
          <MetricValue>{when}</MetricValue>
          <Typography variant="body2" color="text.secondary">{latestDetail}</Typography>
        </MetricCard>
      </Grid>
    </Grid>
  );
}

function MeasuredStorage({ warehouse }) {
  const { t } = useTranslation();
  const query = useWarehouseOverview(warehouse.public_id);
  const storage = query.data?.storage;
  const measured = typeof storage?.bytes === 'number' && Number.isFinite(storage.bytes) && storage.bytes >= 0;
  let detail = t('data.workspaceHome.storageNotMeasured', { defaultValue: 'Not measured yet' });
  if (query.error) detail = t('data.workspaceHome.storageUnavailable', { defaultValue: 'Usage could not be retrieved' });
  else if (query.isLoading) detail = t('data.workspaceHome.storageChecking', { defaultValue: 'Checking usage…' });
  else if (measured) detail = storage.measured_at
    ? t('data.workspaceHome.storageLastMeasured', { date: whenLabel(storage.measured_at) })
    : t('data.workspaceHome.storageMeasurementDateUnknown');
  const showSize = !query.error && !query.isLoading && measured;
  return (
    <Box sx={{ minWidth: 0 }} aria-live="polite">
      <MetricValue>{showSize ? formatBytes(storage.bytes) : '—'}</MetricValue>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{warehouse.name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{detail}</Typography>
    </Box>
  );
}

function StorageSummary() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const query = useWarehouses();
  const lakehouses = (query.data || []).filter((warehouse) => warehouse.kind === 'lakehouse' && warehouse.is_active);
  // No overview fan-out across BYO engines: snapshots concern the managed lakehouse.
  return (
    <MetricCard label={t('data.workspaceHome.storageVolume')} icon={StorageOutlinedIcon} to={isAdmin ? STORAGE_URL : undefined}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {query.error ? <Typography variant="body2">{t('data.workspaceHome.storageUnavailable', { defaultValue: 'Usage could not be retrieved' })}</Typography>
          : query.isLoading ? <Typography variant="body2">{t('data.workspaceHome.storageChecking', { defaultValue: 'Checking usage…' })}</Typography>
            : lakehouses.length ? lakehouses.map((warehouse) => <MeasuredStorage key={warehouse.public_id} warehouse={warehouse} />)
              : <Typography variant="body2">{t('data.workspaceHome.storageInSettings', { defaultValue: 'Storage destinations are available on the Storage tab.' })}</Typography>}
      </Box>
    </MetricCard>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const tokens = useTokens();
  const stats = useStats();
  const connections = useConnections();
  const pipelines = usePipelines();
  const health = usePipelinesHealth();
  const projects = useDbtProjects();
  const firstName = useAuthStore((state) => state.user)?.name?.split(' ')[0];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.6rem', mb: 0.5 }}>{greeting(new Date().getHours(), t)}{firstName ? ', ' + firstName : ''}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.inkMuted }}>{t('data.workspaceHome.intro', { defaultValue: 'Your loads and data preparation in one place.' })}</Typography>
        </Box>
        <Button variant="contained" component={RouterLink} to="/console/connectors/new">{t('shell.connectSource')}</Button>
      </Box>
      <DashboardMetrics connections={connections} pipelines={pipelines} health={health} />
      <LoadStatus summary={getLoadSummary(pipelines, health)} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RunPanel title={t('shell.recentLoads')} linkLabel={t('shell.allLoads')} linkTo={LOADS_URL} empty={t('shell.noLoads')} loading={stats.isLoading} error={stats.error}>
            {(stats.data?.recent_pipeline_runs || []).map((run) => <RunRow key={run.public_id} name={run.pipeline_name} detail={whenLabel(runTime(run))} status={run.status} to={LOADS_URL} />)}
          </RunPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <RunPanel
            title={t('data.workspaceHome.recentPreparation', { defaultValue: 'Recent data preparation' })}
            linkLabel={t('data.workspaceHome.allPreparation', { defaultValue: 'All preparation' })}
            linkTo={PREPARATION_URL}
            empty={t('data.workspaceHome.noPreparationRuns', { defaultValue: 'No preparation runs yet.' })}
            loading={stats.isLoading}
            error={stats.error}
            notice={<PreparationNotice summary={getPreparationSummary(projects)} />}
          >
            {(stats.data?.recent_dbt_runs || []).map((run) => <RunRow key={run.public_id} name={run.project_name} detail={preparationAction(run.command, t) + ' · ' + whenLabel(runTime(run))} status={run.status} to={PREPARATION_URL} />)}
          </RunPanel>
        </Grid>
      </Grid>
    </Box>
  );
}
